import { CircularProgress, Grid } from '@mui/material';
import { validateEmail } from '@nx-smartmonkey/shared/helpers';
import {
  HSpacer,
  RoutalAvatar,
  RoutalDialog,
  RoutalLoader,
  RoutalPalette,
  RoutalSelect,
  RoutalSelectMultiSelectOption,
  VSpacer,
} from '@nx-smartmonkey/ui';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { currencyToSymbol, totalPriceWithDiscount } from '../../../constants/Pricing';
import { OrganizationUserRoles } from '../../../domain/organization/OrganizationUserRoles';
import { useCreateOrganizationUser } from '../../../hooks/organization/useCreateOrganizationUser';
import { useRetrieveOrganization } from '../../../hooks/organization/useRetrieveOrganization';
import { useRetrieveOrganizationResources } from '../../../hooks/organization/useRetrieveOrganizationResources';
import { useRetrieveFlatProjects } from '../../../hooks/project/useRetrieveFlatProjects';
import { useShowSnackbarMessage } from '../../../hooks/snackbarMessage/useShowSnackbarMessage';
import { useRetrieveSubscriptionPricing } from '../../../hooks/subscription/useRetrieveSubscriptionPricing';
import { useRetrieveUser } from '../../../hooks/user/useRetrieveUser';
import Button from '../../../react-components/Button';
import InputText from '../../../react-components/InputText';
import Text from '../../../react-components/Text';
import { getUploadAsBase64 } from '../../../services/requests';

type OrganizationNewUserDialogProps = {
  onClose: (...args: any[]) => any;
  isOpen?: boolean;
  refreshProjects?: boolean; // TODO Check if it is necessary, since it is not being used
  selectedProjectId?: string;
};

export const OrganizationNewUserDialog = ({
  isOpen = false,
  onClose,
  selectedProjectId,
}: OrganizationNewUserDialogProps) => {
  const intl = useIntl();
  const { showSnackbarMessage } = useShowSnackbarMessage();
  const { data: organization } = useRetrieveOrganization();
  const { data: organizationResources } = useRetrieveOrganizationResources();
  const { data: user } = useRetrieveUser();
  const { data: pricing, isLoading: gettingPricing } = useRetrieveSubscriptionPricing({
    billingPeriod: organization?.subscription.billing_period,
  });
  const {
    isLoading: creatingUser,
    success: createUserSuccess,
    run: createOrganizationUser,
  } = useCreateOrganizationUser();
  const { data: projects } = useRetrieveFlatProjects({ user });
  const [firstName, setFirstName] = useState<string>(``);
  const [lastName, setLastName] = useState<string>(``);
  const [role, setRole] = useState<OrganizationUserRoles>(`organization_administrator`);
  const [email, setEmail] = useState<string>(``);
  const [disabledSubmitButton, setDisabledSubmitButton] = useState<boolean>(false);
  const [assignedProjects, setAssignedProjects] = useState<string[]>([]);

  useEffect(() => {
    if (projects && selectedProjectId) {
      setAssignedProjects([selectedProjectId]);
    }
  }, [selectedProjectId, projects, setAssignedProjects]);

  useEffect(() => {
    if (createUserSuccess) {
      closeDialog(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createUserSuccess]);

  useEffect(() => {
    if (!creatingUser && !createUserSuccess) {
      setDisabledSubmitButton(false);
    }
  }, [createUserSuccess, creatingUser]);

  if (!organization || !organizationResources) {
    return null;
  }

  const closeDialog = (refetch: boolean) => {
    setFirstName(``);
    setLastName(``);
    setRole(`organization_administrator`);
    setEmail(``);
    setAssignedProjects([]);
    setDisabledSubmitButton(false);
    onClose(refetch ?? false);
  };

  const handleCreateUser = async () => {
    if (assignedProjects && assignedProjects.length > 0) {
      setDisabledSubmitButton(true);

      createOrganizationUser({ email, firstName, lastName, role, projects: assignedProjects });
    } else {
      showSnackbarMessage({
        messageIntlId: `infoerror.new_organization_user.assigned_projects_needed`,
      });
    }
  };

  const isFormDisabled = () => {
    const noFirstName = !firstName || firstName === ``;
    const noLastName = !lastName || lastName === ``;
    const noEmail = !email || email === `` || !validateEmail(email);
    return (
      disabledSubmitButton ||
      noFirstName ||
      noLastName ||
      noEmail ||
      assignedProjects.length === 0 ||
      organizationResources.exceedsUsersHardLimit(1)
    );
  };

  const getTotalPrice = () => {
    if (!pricing) return 0;

    const paidUsers = Math.max(1 - pricing.missing_extra.users, 0);

    return totalPriceWithDiscount({
      price: paidUsers * pricing.users,
      amountOff: pricing.discount?.amount_off,
      percentOff: pricing.discount?.percent_off,
    });
  };

  return (
    <RoutalDialog
      open={!!isOpen}
      onCloseDialog={closeDialog}
      dialogProps={{
        disableBackdropClick: true,
        disableEscapeKeyDown: true,
        maxWidth: `lg`,
      }}
      contentStyles={{
        padding: 0,
      }}
      titleContent={<Text variant="h6" intlId="settings.organization.users.new.title" />}
      dialogContentWarning={<div />}
      showFooter={false}
    >
      <Grid container style={{ height: `600px` }}>
        {gettingPricing ? (
          <Grid
            item
            container
            direction="column"
            style={{ height: `100%`, width: `100%` }}
            alignItems="center"
            justifyContent="center"
          >
            <RoutalLoader />
          </Grid>
        ) : (
          <>
            {pricing ? (
              <>
                <Grid item xs={8} container>
                  <Grid container direction="column" wrap="nowrap" style={{ padding: `20px 40px` }}>
                    <InputText
                      id="first_name"
                      type="text"
                      label={<Text tagName="span" intlId="settings.organization.users.new.first_name" />}
                      required
                      fieldValue={firstName}
                      onChange={(value: any) => setFirstName(value)}
                    />

                    <VSpacer large />

                    <InputText
                      id="lastName"
                      label={<Text tagName="span" intlId="settings.organization.users.new.last_name" />}
                      type="text"
                      required
                      fieldValue={lastName}
                      onChange={(value: any) => setLastName(value)}
                    />

                    <VSpacer large />

                    <InputText
                      id="email"
                      label={<Text tagName="span" intlId="login.email" />}
                      type="email"
                      required
                      fieldValue={email}
                      onChange={(value: any) => setEmail(value)}
                    />

                    <VSpacer large />

                    <Grid container spacing={1}>
                      <Grid xs={12} md={6} item container direction="column">
                        <Text
                          variant="body"
                          style={{ marginBottom: `12px` }}
                          intlId="settings.organization.users.organization_role"
                        />

                        <RoutalSelect
                          id="user-role-select"
                          containerStyle={{
                            width: `300px`,
                          }}
                          disabled={disabledSubmitButton}
                          popoverProps={{
                            options: [
                              {
                                key: `organization_administrator`,
                                listItemStyles: {
                                  whiteSpace: `normal` as any,
                                  overflow: `auto`,
                                  color: `#929599`,
                                  height: `100%`,
                                  padding: `8px 16px`,
                                  borderBottom: `1px solid #E0E0E0`,
                                  ...(role === `organization_administrator` ? { backgroundColor: `#f8671d14` } : {}),
                                },
                                content: (
                                  <Grid container direction="column" wrap="nowrap">
                                    <Text
                                      style={{ cursor: `pointer` }}
                                      variant="body-medium"
                                      intlId="settings.organization.users.roles.administrator"
                                    />
                                    <VSpacer small />
                                    <Text
                                      style={{ cursor: `pointer` }}
                                      variant="body"
                                      intlId="settings.organization.users.roles.administrator.description"
                                    />
                                  </Grid>
                                ),
                                onClick: () => setRole(`organization_administrator`),
                              },
                              {
                                key: `organization_manager`,
                                listItemStyles: {
                                  whiteSpace: `normal` as any,
                                  overflow: `auto`,
                                  color: `#929599`,
                                  height: `100%`,
                                  padding: `8px 16px`,
                                  borderBottom: `1px solid #E0E0E0`,
                                  ...(role === `organization_manager` ? { backgroundColor: `#f8671d14` } : {}),
                                },
                                content: (
                                  <Grid container direction="column" wrap="nowrap">
                                    <Text
                                      style={{ cursor: `pointer` }}
                                      variant="body-medium"
                                      intlId="settings.organization.users.roles.manager"
                                    />
                                    <VSpacer small />
                                    <Text
                                      style={{ cursor: `pointer` }}
                                      variant="body"
                                      intlId="settings.organization.users.roles.manager.description"
                                    />
                                  </Grid>
                                ),
                                onClick: () => setRole(`organization_manager`),
                              },
                              {
                                key: `organization_user`,
                                listItemStyles: {
                                  whiteSpace: `normal` as any,
                                  overflow: `auto`,
                                  color: `#929599`,
                                  height: `100%`,
                                  padding: `8px 16px`,
                                  ...(role === `organization_user` ? { backgroundColor: `#f8671d14` } : {}),
                                },
                                content: (
                                  <Grid container direction="column" wrap="nowrap">
                                    <Text
                                      style={{ cursor: `pointer` }}
                                      variant="body-medium"
                                      intlId="settings.organization.users.roles.user"
                                    />
                                    <VSpacer small />
                                    <Text
                                      style={{ cursor: `pointer` }}
                                      variant="body"
                                      intlId="settings.organization.users.roles.user.description"
                                    />
                                  </Grid>
                                ),
                                onClick: () => setRole(`organization_user`),
                              },
                            ],
                          }}
                        >
                          <Text
                            variant="body"
                            style={{ cursor: `pointer`, width: `100%`, textAlign: `start` }}
                            intlId={`settings.organization.users.roles.${
                              role === `organization_administrator`
                                ? `administrator`
                                : role === `organization_manager`
                                ? `manager`
                                : `user`
                            }`}
                          />
                        </RoutalSelect>
                      </Grid>

                      <Grid xs={12} md={6} item container direction="column">
                        <Text
                          variant="body"
                          style={{ marginBottom: `12px` }}
                          intlId="settings.organization.users.assigned_projects"
                        />

                        <RoutalSelect
                          id="select-project"
                          containerStyle={{
                            width: `300px`,
                          }}
                          popoverProps={{
                            options: [
                              ...(projects ?? []).map((project): RoutalSelectMultiSelectOption => {
                                return {
                                  key: project.id,
                                  hideCheckIcon: false,
                                  closeOnClick: false,
                                  checked: assignedProjects.includes(project.id),
                                  onClick: (event: any) => {
                                    if (assignedProjects.includes(project.id)) {
                                      setAssignedProjects([
                                        ...assignedProjects.filter(
                                          (assignedProject: string) => assignedProject !== project.id
                                        ),
                                      ]);
                                    } else {
                                      setAssignedProjects([...assignedProjects, project.id]);
                                    }
                                  },
                                  content: (
                                    <Grid container alignItems="center" wrap="nowrap">
                                      <RoutalAvatar
                                        url={project.avatar_url}
                                        getUploadAsBase64={getUploadAsBase64}
                                        label={project.label}
                                        labelStyles={{ fontSize: `10px`, lineHeight: 2 }}
                                        containerStyles={{ width: `24px`, height: `24px` }}
                                      />
                                      <Grid
                                        item
                                        style={{
                                          whiteSpace: `nowrap`,
                                          textOverflow: `ellipsis`,
                                          overflow: `hidden`,
                                          width: `224px`,
                                        }}
                                      >
                                        <span style={{ marginLeft: `8px` }}>{project.label ?? ``}</span>
                                      </Grid>
                                    </Grid>
                                  ),
                                };
                              }),
                            ],
                          }}
                        >
                          <Text
                            variant="body"
                            style={{ cursor: `pointer`, width: `100%`, textAlign: `start` }}
                            intlId="settings.organization.users.assigned_projects_selected"
                            intlValues={{ projects: assignedProjects.length ?? 0 }}
                          />
                        </RoutalSelect>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>

                <Grid
                  item
                  xs={4}
                  container
                  style={{
                    backgroundColor: RoutalPalette.grey9,
                    padding: `20px 40px`,
                  }}
                  direction="column"
                  alignItems="center"
                  justifyContent="center"
                >
                  {organization.subscription.status !== `trialing` ? (
                    <>
                      <Grid item>
                        {organization.subscription.has_contract ? (
                          organizationResources.exceedsUsersHardLimit(1) ? (
                            <Text
                              variant="body-medium"
                              intlId={`settings.organization.users.create.contract_exceeded`}
                              style={{
                                textAlign: `center`,
                                color: RoutalPalette.error.medium,
                              }}
                            />
                          ) : (
                            <Text
                              intlId={`settings.organization.users.create.contract_info`}
                              intlValues={{
                                restantes: Math.max(
                                  organizationResources.subscription.users - organizationResources.getUsersCount() - 1,
                                  0
                                ),
                              }}
                              style={{
                                textAlign: `center`,
                              }}
                            />
                          )
                        ) : (
                          <Text
                            intlId={`settings.organization.users.create.summary_info`}
                            intlValues={{
                              price: `${getTotalPrice()}${currencyToSymbol(pricing.currency)} ${
                                organization.subscription.billing_period
                                  ? intl.formatMessage({ id: `subscription.update.per_month` }).toLocaleLowerCase()
                                  : intl.formatMessage({ id: `subscription.update.per_year` }).toLocaleLowerCase()
                              }`,
                            }}
                            style={{
                              textAlign: `center`,
                            }}
                          />
                        )}
                      </Grid>

                      <VSpacer medium />
                    </>
                  ) : null}

                  <Grid item container direction="row" alignItems="center" justifyContent="center">
                    <Grid item>
                      <Button
                        intlId="cancel"
                        variant="flat"
                        styleType="secondary"
                        style={{
                          height: `14px`,
                          minWidth: `100px`,
                        }}
                        onClick={() => closeDialog(false)}
                      />
                    </Grid>

                    <HSpacer small />

                    <Grid item>
                      <Button
                        disabled={isFormDisabled()}
                        style={{
                          minWidth: `100px`,
                          height: `16px`,
                        }}
                        onClick={handleCreateUser}
                      >
                        {creatingUser ? (
                          <CircularProgress size={20} />
                        ) : (
                          <Text variant="button" intlId="create" style={{ color: `white` }} />
                        )}
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
              </>
            ) : null}
          </>
        )}
      </Grid>
    </RoutalDialog>
  );
};
